<template>
  <div class="credential">
    <div class="d-flex justify-space-between">
      <h2>{{credential.name.value}}</h2>

      <v-chip
        :color="expirationChip.color"
        label
      >{{ expirationChip.text }}</v-chip>
    </div>

    <v-form class="form" ref="form">
      <div v-for="(field, index2) in Object.keys(credential)" :key="index2">
        <div class="d-flex align-start" v-if="credential[field].display">
          <p class="credential-name">{{credential[field].label}}</p>
          <p class="flex-grow-1 mx-4 mb-1">
            <v-text-field
              v-if="credential[field].type === 'text'"
              :id="'credential-value-' + index1 + '-' + index2"
              required
              outlined
              height="46"
              dense
              class="text-field-outlined-overrides"
              append-icon="mdi-content-copy"
              @click:append="clickCopy(credential[field].value)"
              v-model="credential[field].value"
              :readonly="!credential[field].editing"
              :rules="validation.notEmpty"
            ></v-text-field>

            <v-textarea
              v-if="credential[field].type === 'array' && credential[field].value"
              :id="'credential-value-' + index1 + '-' + index2"
              required
              outlined
              dense
              class="text-field-outlined-overrides"
              append-icon="mdi-content-copy"
              @click:append="clickCopy(self()[field])"
              v-model="self()[field]"
              :readonly="!credential[field].editing"
              :rules="validation.notEmpty"
            ></v-textarea>
          </p>
          <p class="credential-edit" v-if="editable">
            <v-btn
              v-if="credential[field].editable && !credential[field].editing"
              @click="enableEditing(credential[field], index1, index2)"
              outlined
              height="46"
              width="100%"
            ><v-icon small class="mr-2">mdi-pencil</v-icon>Edit</v-btn>

            <v-btn
              v-if="credential[field].editable && credential[field].editing"
              @click="disableEditing(credential[field])"
              color="primary"
              height="46"
              width="100%"
            >Done</v-btn>
          </p>
        </div>
      </div>
    </v-form>

    <div
      v-if="shouldShowExtendButton(credential)"
    >
      <v-divider class="mt-2 mb-6"></v-divider>
      <div class="d-flex">
        <v-icon
          class="mr-4"
          size="x-large"
          color="info"
        >mdi-clock-plus-outline</v-icon>
        <p class="ma-0 flex-grow-1 d-flex align-center">Extend the life of this credential by 30 days.</p>
        <v-btn
          color="primary"
          @click="extendCredentialLife(credential)"
        >
          Extend life
        </v-btn>
      </div>
    </div>

    <div class="mt-4" v-if="editable">
      <v-btn
        color="primary"
        width="187"
        height="48"
        class="mr-4"
        @click="updateCredential(credential)"
      >Save changes</v-btn>

      <v-tooltip
        v-if="numCredentials === 1"
        bottom
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            width="187"
            height="48"
            v-bind="attrs"
            v-on="on"
          >
            Delete Credential
          </v-btn>
        </template>
        <span>You cannot delete your last credential.</span>
      </v-tooltip>

      <v-btn
        v-else
        text
        width="187"
        height="48"
        @click="deleteCredential(credential)"
      >Delete credential</v-btn>
    </div>

    <v-dialog
      v-model="dialogs.delete"
      width="600"
      height="300"
    >
      <v-card class="delete-partner">
        <v-card-title class="pa-8 justify-center">
          <v-icon class="black--text">mdi-alert-circle</v-icon>
        </v-card-title>
        <v-card-text
          class="pb-8 grey--text text--darken-4"
        >
          <p class="mb-8">Are you sure you want to remove the credential <strong>{{credential.name.value}}</strong>?</p>

          <ul class="mb-8">
            <li>Applications using this credential will not be able to access the API or SDK.</li>
          </ul>

          <v-btn
            width="187"
            height="48"
            class="mr-5"
            dark
            @click="deleteCredentialConfirm(credential)"
          >Delete credential</v-btn>
          <v-btn
            width="187"
            height="48"
            outlined
            @click="dialogs.delete = false"
          >Cancel</v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import humanizeDuration from 'humanize-duration';

import constants from 'august-constants';

import config from '../config';
import util from '../util';

export default {
  name: 'Credential',
  props: [
    'initialCredential',
    'index1',
    'numCredentials',
    'editable'
  ],
  data() {
    return {
      constants,
      credential: this.initialCredential,
      validation: config.formValidations,
      dialogs: {
        delete: false,
      },
    };
  },
  computed: {
    // The redirect URIs are stored as an array, but displayed as a string.
    redirectUris: {
      get() {
        return this.credential['redirectUris'].value.join('\n');
      },
      set(value) {
        this.credential['redirectUris'].value = value.split('\n');
      },
    },

    timeUntilExpiration: function () {
      const expiresInMS = this.credential.disableAt?.value - Date.now();

      return humanizeDuration(expiresInMS, {
        units: ['d'],
        round: true,
      });
    },

    expirationChip: function () {
      const now = Date.now();

      if (!this.credential.disableAt.value) {
        return {
          color: 'success',
          text: 'Never expires',
        }
      } else if (this.credential.disableAt.value > now) {
        return {
          color: 'warning',
          text: `Expires in ${this.timeUntilExpiration}`,
        }
      }

      return {
        color: 'error',
        text: 'Expired',
      }
    },
  },
  methods: {
    self() {
      return this;
    },
    clickCopy: util.clickCopy,
    enableEditing: function (credentialProperty, index1, index2) {
      credentialProperty.editing = true;

      const input = document.querySelector(`#credential-value-${index1}-${index2}`);
      if (input) {
        setTimeout(() => input.focus());
      }
    },
    disableEditing: function (credentialProperty) {
      credentialProperty.editing = false;
    },
    disableAllEditing: function () {
      for (const key of Object.keys(this.credential)) {
        this.credential[key].editing = false;
      }
    },
    updateCredential: async function (credential) {
      const isValid = this.$refs.form.validate();
      if (!isValid) {
        return;
      }

      const data = {}
      for (const [credentialName, credentialObj] of Object.entries(credential)) {
        if (credentialObj.editable) {
          data[credentialName] = credentialObj.value;
        }
      }

      try {
        await util.axios({
          url: `${this.$store.state.apiHost}/credentials/${credential.clientID.value}`,
          method: 'put',
          data,
        });

        this.$store.commit('snackbar', {text: 'Credential saved', color: 'success'});
        this.disableAllEditing();
      } catch (error) {
        this.$store.commit('snackbar', {text: 'Failed to save credential', color: 'error'});
      }
    },
    deleteCredential: function () {
      this.dialogs.delete = true;
    },
    deleteCredentialConfirm: async function (credential) {
      const options = {
        method: 'DELETE',
        ...config.defaultFetchOptions,
      };
      const result = await util.fetch(`${this.$store.state.apiHost}/credentials/${credential.clientID.value}`, options);

      if (result.ok) {
        this.$store.commit('snackbar', {text: 'Credential deleted', color: 'success'});
        this.dialogs.delete = false;
        this.$emit('credential-deleted', credential);
      } else {
        const error = await result.json();
        if (result.status === 409) {
          this.$store.commit('snackbar', {text: error.message, color: 'error'});
        } else {
          this.$store.commit('snackbar', {text: 'Failed to delete credential', color: 'error'});
        }
      }
    },
    extendCredentialLife: async function (credential) {
      try {
        const result = await util.axios({
          url: `${this.$store.state.apiHost}/credentials/${credential.clientID.value}/extend`,
          method: 'post',
        });
        const newTimestamp = result.data.timestamp;
        credential.disableAt.value = newTimestamp;
        credential.disableAtPretty.value = new Date(newTimestamp);
        credential.disabled.value = false;

        this.$store.commit('snackbar', {text: 'Credential life extended', color: 'success'});
      } catch (error) {
        console.log(error);
        this.$store.commit('snackbar', {text: 'Failed to extend credential life', color: 'error'});
      }
    },
    shouldShowExtendButton (credential) {
      return this.$store.state.role === constants.DeveloperRoles.GLOBAL_ADMIN && credential.disableAt?.value;
    }
  },
};
</script>

<style lang="scss">
.credential {
  .credential-name {
    width: 8rem;
    font-weight: bold;
    margin: 12px 0;
  }
  .credential-edit {
    width: 100px;
  }
}
</style>